import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser, getTelegramLink, getWhatsAppLink, getGoogleAuthUrl, unlinkGoogle, getUserReminders, createReminder, cancelSubscription, unlinkTelegram, unlinkWhatsApp, UserResponse, ReminderItem, getMyNotifications } from '../services/api'; // Added WhatsApp services
import Chat from '../components/Chat';
import SupportFloatingButton from '../components/SupportFloatingButton';
import SupportChatOverlay from '../components/SupportChatOverlay';
import './DashboardPage.css';

const DashboardPage = () => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [telegramLink, setTelegramLink] = useState<string | null>(null);
  const [whatsappLink, setWhatsappLink] = useState<string | null>(null);
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  const [newReminderMsg, setNewReminderMsg] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  // Load last read timestamp from local storage
  const getLastReadTime = () => {
    const stored = localStorage.getItem('cuda_last_read_notif');
    return stored ? new Date(stored).getTime() : 0;
  };

  const updateUnreadCount = (currentNotifs: any[]) => {
    const lastRead = getLastReadTime();
    const unread = currentNotifs.filter(n => new Date(n.created_at).getTime() > lastRead).length;
    setUnreadCount(unread);
  };

  const handleOpenNotifications = () => {
    setShowNotifications(true);
    // Mark as read effectively
    localStorage.setItem('cuda_last_read_notif', new Date().toISOString());
    setUnreadCount(0);
  };

  const fetchReminders = async () => {
    const fetchedReminders = await getUserReminders();
    if (fetchedReminders) {
      setReminders(fetchedReminders);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("¿Estás seguro de que deseas cancelar tu suscripción? Seguirá activa hasta el final del periodo actual.")) return;
    try {
      await cancelSubscription();
      const updatedUser = await getCurrentUser();
      if (updatedUser) setUser(updatedUser);
      alert("Suscripción cancelada exitosamente.");
    } catch (err: any) {
      alert("Error al cancelar suscripción: " + err.message);
    }
  };

  const handleUnlinkTelegram = async () => {
    if (!window.confirm("¿Estás seguro de que deseas desvincular tu cuenta de Telegram? No recibirás más recordatorios por esa vía.")) return;
    try {
      await unlinkTelegram();
      const updatedUser = await getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
        // Refresh telegram link since token changed
        const linkData = await getTelegramLink();
        if (linkData) setTelegramLink(linkData.link);
      }
      alert("Telegram desvinculado exitosamente.");
    } catch (err: any) {
      alert("Error al desvincular Telegram: " + err.message);
    }
  };

  const handleConnectTelegram = () => {
    setShowTelegramModal(true);
  };

  const confirmConnectTelegram = () => {
    setShowTelegramModal(false);
    if (telegramLink) {
      window.open(telegramLink, '_blank');
    }
  };

  const handleUnlinkWhatsApp = async () => {
    if (!window.confirm("¿Deseas desvincular WhatsApp?")) return;
    try {
      await unlinkWhatsApp();
      const updatedUser = await getCurrentUser();
      if (updatedUser) setUser(updatedUser);
      alert("WhatsApp desvinculado.");
    } catch (err: any) { alert(err.message); }
  };

  const handleConnectWhatsApp = async () => {
    try {
      if (user?.telegram_id) {
         if (!window.confirm("¿Seguro que quieres pasar a WhatsApp? Telegram se desvinculará.")) return;
      }
      const data = await getWhatsAppLink();
      if (data && data.link) {
          // Guardar el link web como fallback
          if (data.link_web) setWhatsappLink(data.link_web);
          // Usar protocolo directo whatsapp:// para abrir la app sin página intermedia
          window.location.href = data.link;
      }
    } catch (err: any) { alert(err.message); }
  };

  const handleConnectGoogle = async () => {
    try {
      const data = await getGoogleAuthUrl();
      if (data && data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err: any) {
      alert("Error al conectar con Google: " + err.message);
    }
  };

  const handleUnlinkGoogle = async () => {
    if (!window.confirm("¿Deseas desvincular tu cuenta de Google? Tu asistente ya no podrá acceder a tu calendario ni contactos.")) return;
    try {
      await unlinkGoogle();
      const updatedUser = await getCurrentUser();
      if (updatedUser) setUser(updatedUser);
      alert("Google desvinculado exitosamente.");
    } catch (err: any) {
      alert("Error al desvincular Google: " + err.message);
    }
  };


  const handleCreateReminder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReminderMsg || !newReminderDate) return;
    try {
      if (user?.plan === 'free') {
        alert("Tu plan actual (Free) no incluye recordatorios proactivos. Por favor, adquiere el plan Medio o Full para activar esta función.");
        return;
      }
      await createReminder(newReminderMsg, newReminderDate);
      setNewReminderMsg('');
      setNewReminderDate('');
      fetchReminders(); // Refresh list
    } catch (err: any) {
      alert("Error creando recordatorio: " + err.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          if (!currentUser.telegram_id) {
            const linkData = await getTelegramLink();
            if (linkData) setTelegramLink(linkData.link);
          }
          if (!currentUser.whatsapp_linked) {
            const waData = await getWhatsAppLink();
            if (waData) setWhatsappLink(waData.link);
          }
          // Fetch reminders
          const fetchedReminders = await getUserReminders();
          if (fetchedReminders) {
            setReminders(fetchedReminders);
          }
          // Fetch notifications
          const fetchedNotifs = await getMyNotifications();
          if (fetchedNotifs) {
            if (fetchedNotifs) {
              setNotifications(fetchedNotifs);
              updateUnreadCount(fetchedNotifs);
            }
          }
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Poll for updates every 30 seconds
    const interval = setInterval(async () => {
      fetchReminders();
      const fetchedNotifs = await getMyNotifications();
      if (fetchedNotifs) {
        setNotifications(fetchedNotifs);
        // Only update unread count if sidebar is closed, otherwise we are "reading" them
        if (!showNotifications) {
          updateUnreadCount(fetchedNotifs);
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const renderContent = () => {
    if (loading) {
      return <p>Cargando dashboard...</p>;
    }

    if (error) {
      return <div className="alert alert-danger">{error}</div>;
    }

    if (user) {
      return (
        <div>
          <h2>¡Bienvenido, {user.full_name.split(' ')[0]}!</h2>
          <p>Este es tu espacio personal. El nombre de tu asistente es <strong>{user.assistant_name}</strong>.</p>

          {/* Notification Sidebar Toggle */}
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-outline-primary position-relative"
              onClick={handleOpenNotifications}
            >
              <i className="bi bi-bell-fill"></i> Notificaciones
              {unreadCount > 0 && <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                {unreadCount}
                <span className="visually-hidden">unread messages</span>
              </span>}
            </button>
          </div>

          {/* Render Sidebar */}
          <div className={`notification-sidebar ${showNotifications ? 'open' : ''}`}>
            <div className="sidebar-header">
              <h5>Notificaciones</h5>
              <button className="btn-close" onClick={() => setShowNotifications(false)}></button>
            </div>
            <div className="sidebar-content">
              {notifications.length === 0 ? (
                <p className="text-muted text-center mt-5">No tienes notificaciones.</p>
              ) : (
                notifications.map((notif) => (
                  <div key={notif.id} className="notification-bubble-container mb-3 animate__animated animate__fadeIn">
                    <small className="text-muted d-block mb-1 ms-1">{new Date(notif.created_at).toLocaleString()}</small>
                    <div className="notification-bubble">
                      <p className="mb-0">{notif.message}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          {/* Overlay */}
          {showNotifications && <div className="notification-overlay" onClick={() => setShowNotifications(false)}></div>}

          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card h-100 shadow-sm border-0 platform-card">
                <div className="card-body">
                  <h5 className="card-title d-flex align-items-center gap-2">
                    <i className="bi bi-telegram text-primary"></i> Telegram
                  </h5>
                  <div className="mt-3">
                    {user.telegram_id ? (
                      <div>
                        <p className="text-success small mb-3"><i className="bi bi-check-circle-fill me-2"></i>Vinculado correctamente.</p>
                        <button onClick={handleUnlinkTelegram} className="btn btn-outline-danger btn-sm w-100">
                          Desvincular Telegram
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-muted small mb-3">Recibe avisos y chatea con {user.assistant_name} desde Telegram.</p>
                        <button onClick={handleConnectTelegram} className="btn btn-telegram btn-sm w-100">
                          {user.whatsapp_linked ? "Cambiar a Telegram" : "Vincular Telegram"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card h-100 shadow-sm border-0 platform-card">
                <div className="card-body">
                  <h5 className="card-title d-flex align-items-center gap-2">
                    <i className="bi bi-whatsapp text-success"></i> WhatsApp
                  </h5>
                  <div className="mt-3">
                    {user.whatsapp_linked ? (
                      <div>
                        <p className="text-success small mb-3"><i className="bi bi-check-circle-fill me-2"></i>Vinculado correctamente.</p>
                        <button onClick={handleUnlinkWhatsApp} className="btn btn-outline-danger btn-sm w-100">
                          Desvincular WhatsApp
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-muted small mb-3">Lleva a {user.assistant_name} en tu bolsillo a través de WhatsApp.</p>
                        <button onClick={handleConnectWhatsApp} className="btn btn-sm w-100 text-white mb-2" style={{backgroundColor: '#25D366', borderColor: '#25D366'}}>
                          <i className="bi bi-whatsapp me-2"></i>{user.telegram_id ? "Cambiar a WhatsApp" : "Vincular WhatsApp"}
                        </button>
                        {whatsappLink && (
                          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="d-block text-center text-muted small">
                            ¿No abre? Tocá aquí como alternativa
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card h-100 shadow-sm border-0 platform-card" style={{borderLeft: '4px solid #4285F4'}}>
                <div className="card-body">
                  <h5 className="card-title">
                    <i className="bi bi-google text-primary"></i> Google Sync
                  </h5>

                  <div className="mt-3">
                    {user.google_linked ? (
                      <div>
                        <p className="text-primary small mb-2"><i className="bi bi-check-circle-fill me-2"></i>Calendario y Contactos vinculados.</p>
                        <p className="text-muted x-small mb-3" style={{fontSize: '0.75rem'}}>Tu asistente puede leer tu agenda y pasar contactos en vivo.</p>
                        <div className="d-flex gap-2">
                           <button onClick={handleUnlinkGoogle} className="btn btn-outline-danger btn-sm flex-grow-1">
                             Desvincular Google
                           </button>
                           <button onClick={handleConnectGoogle} className="btn btn-outline-secondary btn-sm">
                             Actualizar
                           </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-muted small mb-3">Sincroniza tu calendario y contactos para habilitar el asistente manos libres.</p>
                        <button onClick={handleConnectGoogle} className="btn btn-outline-primary btn-sm w-100">
                          Vincular Google
                        </button>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>


            <div className="col-md-6">
              <div className="card mt-4 shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title d-flex justify-content-between">
                    Suscripción
                    <span className={`badge ${user.subscription?.is_cancelled ? 'bg-warning' : 'bg-primary'}`}>
                      {user.plan.toUpperCase()}
                    </span>
                  </h5>

                  {user.subscription && (
                    <div className="mt-3">
                      <p className="mb-1">Mensajes: <strong>{user.subscription.message_count}</strong> {user.subscription.message_limit ? `/ ${user.subscription.message_limit}` : '(Ilimitados)'}</p>
                      {user.subscription.message_limit && (
                        <div className="progress mb-3" style={{ height: '8px' }}>
                          <div
                            className={`progress-bar ${(user.subscription.message_count / user.subscription.message_limit) > 0.8 ? 'bg-danger' : 'bg-success'}`}
                            role="progressbar"
                            style={{ width: `${Math.min(100, (user.subscription.message_count / user.subscription.message_limit) * 100)}%` }}
                          ></div>
                        </div>
                      )}

                      {user.subscription.message_limit && (user.subscription.message_limit - user.subscription.message_count) <= 5 && (
                        <div className="alert alert-warning py-2 small">
                          Te quedan pocos mensajes. Considera mejorar tu plan.
                        </div>
                      )}

                      {user.plan === 'trial' && user.subscription.cycle_end_date && (
                        <div className="alert alert-info py-2 small">
                          Tu prueba termina el {new Date(user.subscription.cycle_end_date).toLocaleDateString()}.
                        </div>
                      )}

                      {user.subscription.is_cancelled ? (
                        <p className="text-muted small italic">Tu plan se cancelará el {user.subscription.cycle_end_date ? new Date(user.subscription.cycle_end_date).toLocaleDateString() : 'fin del ciclo'}.</p>
                      ) : (
                        <div className="d-flex gap-2">
                          {user.plan !== 'full' && user.plan !== 'vip' && (
                            <Link to="/pricing" className="btn btn-sm btn-outline-primary">Mejorar Plan</Link>
                          )}
                          {!['free', 'vip', 'trial'].includes(user.plan) && (
                            <button onClick={handleCancelSubscription} className="btn btn-sm btn-link text-danger p-0">Cancelar suscripción</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Reminders Section */}
          <div className="card mt-4">
            <div className="card-body">
              <h5 className="card-title">Tus Recordatorios</h5>

              {/* Add Reminder Form */}
              <form onSubmit={handleCreateReminder} className="mb-4">
                <div className="row g-2">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Mensaje del recordatorio"
                      value={newReminderMsg}
                      onChange={(e) => setNewReminderMsg(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={newReminderDate}
                      onChange={(e) => setNewReminderDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-2 d-grid">
                    <button type="submit" className="btn btn-primary">Agregar</button>
                  </div>
                </div>
              </form>

              {reminders.length === 0 ? (
                <p className="text-muted">No tienes recordatorios pendientes.</p>
              ) : (
                <ul className="list-group">
                  {reminders.map((reminder) => (
                    <li key={reminder.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {reminder.message}
                      <span className="badge bg-primary rounded-pill">
                        {new Date(reminder.reminder_date).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div id="chat-container">
            <Chat assistantName={user.assistant_name} onMessageSent={fetchReminders} />
          </div>

          <SupportChatOverlay
            isOpen={isSupportOpen}
            onClose={() => setIsSupportOpen(false)}
          />
          <SupportFloatingButton
            isOpen={isSupportOpen}
            onClick={() => setIsSupportOpen(!isSupportOpen)}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="light-theme-page">
      <div className="container">
        <h1>Dashboard</h1>
        {renderContent()}

        {/* Telegram Info Modal */}
        {showTelegramModal && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">Vincular con Telegram</h5>
                  <button type="button" className="btn-close" onClick={() => setShowTelegramModal(false)}></button>
                </div>
                <div className="modal-body py-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                      <i className="bi bi-info-circle-fill text-primary fs-4"></i>
                    </div>
                    <p className="mb-0 fs-5">
                      Tu asistente se llama por defecto <strong>"Mi asistente personal"</strong>.
                    </p>
                  </div>
                  <p className="text-muted">
                    Si deseas cambiar su nombre, podrás hacerlo directamente dentro de Telegram una vez conectado.
                  </p>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button type="button" className="btn btn-light" onClick={() => setShowTelegramModal(false)}>Cancelar</button>
                  <button type="button" className="btn btn-primary px-4" onClick={confirmConnectTelegram}>Entendido, ir a Telegram</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
